import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Button, Col, Row } from 'reactstrap';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import Sidebar from '../../Sidebar';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import FloatingControls from '../../components/FloatingControls';
import { displayError } from '../../helpers';
import {
    cancelReceiptVoucher,
    deleteReceiptVoucher,
    getReceiptVoucherDetails,
} from '../../services/ReceiptVoucherServices';
import { AccountingHintPanel } from './VoucherUtils';
import { fetchAndShowReceiptVoucherPdf } from '../../utils/receiptVoucherPdf';

function ReceiptVoucherEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [record, setRecord] = useState(null);
    const [inProgress, setInProgress] = useState(true);

    useEffect(() => {
        if (!id) {
            return;
        }
        setInProgress(true);
        getReceiptVoucherDetails(cookies, id)
            .then((response) => setRecord(response))
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const handlePrint = () => {
        setInProgress(true);
        fetchAndShowReceiptVoucherPdf(cookies, id, { title: 'Receipt Voucher' })
            .finally(() => setInProgress(false));
    };

    const handleCancel = () => {
        Swal.fire({
            title: 'Cancel receipt voucher?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            cancelReceiptVoucher(cookies, id)
                .then((response) => {
                    Swal.fire('Cancelled', response.message || 'Receipt voucher cancelled.', 'success');
                    return getReceiptVoucherDetails(cookies, id);
                })
                .then((response) => setRecord(response))
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const handleDelete = () => {
        Swal.fire({
            title: 'Delete cancelled receipt voucher?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            deleteReceiptVoucher(cookies, id)
                .then((response) => {
                    Swal.fire('Deleted', response.message || 'Receipt voucher deleted.', 'success');
                    navigate('/ReceiptVoucherTable');
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'))
                .finally(() => setInProgress(false));
        });
    };

    if (!record && inProgress) {
        return (
            <div id="outer-container" className="App">
                <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
                <div id="page-wrap"><LoadingOverlay active /></div>
            </div>
        );
    }

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <FormPageLayout
                    moduleLabel="ACCOUNTS"
                    title="Receipt Voucher"
                    subtitle={record?.receipt_no || ''}
                    modeLabel="VIEW MODE"
                >
                    {record && (
                        <>
                            <AccountingHintPanel hint={record.accounting_hint} />
                            <div className="form-row table-bordered shadow p-3 my-2 border-secondary form-control-panel">
                                <div className="row mb-2">
                                    <div className="col-sm-3"><strong>Plant</strong><div>{record.plant_alias}</div></div>
                                    <div className="col-sm-3"><strong>Receipt No</strong><div>{record.receipt_no}</div></div>
                                    <div className="col-sm-3"><strong>Receipt Date</strong><div>{record.receipt_date}</div></div>
                                    <div className="col-sm-3"><strong>Status</strong><div>{record.status ? 'Active' : 'Cancelled'}</div></div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-sm-4"><strong>Customer</strong><div>{record.customer_name}</div></div>
                                    <div className="col-sm-4"><strong>Bank</strong><div>{record.bank_name}</div></div>
                                    <div className="col-sm-4"><strong>Amount</strong><div>{record.receipt_amount}</div></div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-sm-3"><strong>Mode</strong><div>{record.receipt_mode}</div></div>
                                    <div className="col-sm-3"><strong>Against</strong><div>{record.receipt_against}</div></div>
                                    <div className="col-sm-3"><strong>Bank Date</strong><div>{record.bank_date}</div></div>
                                    <div className="col-sm-3"><strong>Instrument</strong><div>{record.instrument_no || '—'}</div></div>
                                </div>
                                {record.remarks && (
                                    <div className="row"><div className="col-sm-12"><strong>Remarks</strong><div>{record.remarks}</div></div></div>
                                )}
                            </div>
                            {(record.invoice_lines || []).length > 0 && (
                                <div className="table-responsive form-control-panel shadow p-2 my-2">
                                    <table className="table table-sm table-bordered mb-0">
                                        <thead>
                                            <tr>
                                                <th>Invoice No</th>
                                                <th>Date</th>
                                                <th>Receipt Amt</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {record.invoice_lines.map((line) => (
                                                <tr key={line.invoice_id}>
                                                    <td>{line.invoice_no}</td>
                                                    <td>{line.invoice_date}</td>
                                                    <td>{line.receipt_amt}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                <Col className="d-flex justify-content-end flex-wrap">
                                    {record?.status ? (
                                        <>
                                            <Button
                                                color="primary"
                                                type="button"
                                                className="mr-2 mb-2"
                                                onClick={() => navigate(`/ReceiptVoucher/edit/${id}`)}
                                            >
                                                Edit Receipt
                                            </Button>
                                            <Button color="warning" type="button" className="mr-2 mb-2" onClick={handleCancel}>
                                                Cancel Voucher
                                            </Button>
                                        </>
                                    ) : (
                                        <Button color="danger" type="button" className="mr-2 mb-2" onClick={handleDelete}>
                                            Delete
                                        </Button>
                                    )}
                                    <Button
                                        color="success"
                                        type="button"
                                        className="mr-2 mb-2"
                                        onClick={handlePrint}
                                    >
                                        Print
                                    </Button>
                                    <Button color="secondary" type="button" className="mb-2" onClick={() => navigate('/ReceiptVoucherTable')}>
                                        Back to List
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    )}
                </FormPageLayout>
                <LoadingOverlay active={inProgress} />
                <FloatingControls tableLink="/ReceiptVoucherTable" enableCancel={false} />
            </div>
        </div>
    );
}

export default ReceiptVoucherEdit;
